/**
 * Activity Log Utility
 * Handles patient-specific activity logging and retrieval from localStorage
 */

export interface Activity {
  id: string;
  timestamp: number;
  type: "appointment_booked" | "appointment_cancelled" | "appointment_rescheduled" | "referral_received" | "profile_updated" | "report_added" | "medication_added" | "vitals_recorded";
  description: string;
  metadata?: Record<string, any>;
}

const ACTIVITY_LOG_KEY_PREFIX = "vivo_patient";

/**
 * Get the localStorage key for a patient's activity log
 */
function getActivityLogKey(patientId: string): string {
  return `${ACTIVITY_LOG_KEY_PREFIX}_${patientId}_activity_log`;
}

/**
 * Log an activity for a specific patient
 */
export function logActivity(
  patientId: string | undefined,
  type: Activity["type"],
  description: string,
  metadata?: Record<string, any>
): void {
  if (!patientId) {
    console.warn("Cannot log activity: patientId is missing");
    return;
  }

  try {
    const key = getActivityLogKey(patientId);
    const existingLog = localStorage.getItem(key);
    const activities: Activity[] = existingLog ? JSON.parse(existingLog) : [];

    const newActivity: Activity = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      type,
      description,
      metadata,
    };

    // Add new activity and keep only the last 50
    activities.unshift(newActivity);
    const trimmedActivities = activities.slice(0, 50);

    localStorage.setItem(key, JSON.stringify(trimmedActivities));
    console.log(`Activity logged for patient ${patientId}:`, newActivity);
  } catch (error) {
    console.error("Error logging activity:", error);
  }
}

/**
 * Get all activities for a specific patient
 */
export function getActivityLog(patientId: string | undefined): Activity[] {
  if (!patientId) {
    console.warn("Cannot get activity log: patientId is missing");
    return [];
  }

  try {
    const key = getActivityLogKey(patientId);
    const log = localStorage.getItem(key);
    return log ? JSON.parse(log) : [];
  } catch (error) {
    console.error("Error retrieving activity log:", error);
    return [];
  }
}

/**
 * Clear all activities for a specific patient
 */
export function clearActivityLog(patientId: string | undefined): void {
  if (!patientId) {
    console.warn("Cannot clear activity log: patientId is missing");
    return;
  }

  try {
    const key = getActivityLogKey(patientId);
    localStorage.removeItem(key);
    console.log(`Activity log cleared for patient ${patientId}`);
  } catch (error) {
    console.error("Error clearing activity log:", error);
  }
}

/**
 * Get the last N activities for a specific patient
 */
export function getRecentActivities(
  patientId: string | undefined,
  limit: number = 10
): Activity[] {
  if (!patientId) return [];
  const allActivities = getActivityLog(patientId);
  return allActivities.slice(0, limit);
}

/**
 * Format activity for display
 */
export function formatActivityDescription(activity: Activity): string {
  const now = Date.now();
  const diffMs = now - activity.timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  let timeAgo: string;
  if (diffMins < 1) {
    timeAgo = "just now";
  } else if (diffMins < 60) {
    timeAgo = `${diffMins}m ago`;
  } else if (diffHours < 24) {
    timeAgo = `${diffHours}h ago`;
  } else {
    timeAgo = `${diffDays}d ago`;
  }

  return `${activity.description} (${timeAgo})`;
}

/**
 * Get activity icon based on type
 */
export function getActivityIcon(type: Activity["type"]): string {
  const icons: Record<Activity["type"], string> = {
    appointment_booked: "📅",
    appointment_cancelled: "❌",
    appointment_rescheduled: "🔄",
    referral_received: "📨",
    profile_updated: "👤",
    report_added: "📄",
    medication_added: "💊",
    vitals_recorded: "❤️",
  };
  return icons[type] || "•";
}
